// src/components/SysMessageGate.jsx
//
// Mounted once at the App level. On first authenticated page load it asks the
// backend whether there's a pending system message for this user (feature
// announcement, payment link enablement, etc.) and renders the appropriate
// modal. When the user dismisses (either button), it POSTs to the dismiss
// endpoint so the same message never re-appears.
//
// Backend contract:
//   GET /v2/user/get_sys_message.php
//     → { success: true, message: "<code>" }                           // legacy plain code
//       or { success: true, message: { id: number, code: string } }    // preferred
//       or { success: true, message: "{\"id\":1,\"code\":\"...\"}" }   // JSON-as-string
//   POST /v2/user/dismiss_sys_message.php  body: { id }
//     → { success: true }
//
// Codes recognized today: any of the PAYMENT_LINKS aliases, plus
// "takedown_feature_launch" / "new_takedown_feature".

import { useEffect, useRef, useState } from "react";
import FrostedModal from "./modals/FrostedModal";
import WhatsNewModal from "./modals/WhatsNewModal";
import { MODAL_TYPE, MODAL_CONFIG } from "../utils/errors";
import { getToken, redirectToLogin } from "../utils/auth";
import { onWhatsNewOpen } from "../utils/whatsNew";

const SYS_MESSAGE_GET_URL =
  "https://whiteintel.io/api/regular/app/v2/user/get_sys_message.php";
const SYS_MESSAGE_DISMISS_URL =
  "https://whiteintel.io/api/regular/app/v2/user/dismiss_sys_message.php";

// Map a normalized backend code to a MODAL_TYPE. Returns null when the code
// isn't recognized (in which case we silently skip showing a modal — the
// backend may have shipped a code the frontend doesn't know about yet).
function modalTypeForCode(rawCode) {
  const code = String(rawCode || "").trim().toLowerCase();
  if (
    [
      "payment_allowed",
      "payment_links",
      "payment_links_enabled",
      "payment_links_activated",
      "checkout_access_approved",
    ].includes(code)
  ) {
    return MODAL_TYPE.PAYMENT_LINKS;
  }
  if (
    [
      "takedown_feature_launch",
      "new_takedown_feature",
      "takedown_announcement",
    ].includes(code)
  ) {
    return MODAL_TYPE.NEW_TAKEDOWN_FEATURE;
  }
  if (
    [
      "whats_new",
      "whats_new_release",
      "whats_new_june",
      "whats_new_june_2026",
      "release_june_2026",
      "whats_new_may_2026",
      "release_may_2026",
    ].includes(code)
  ) {
    return MODAL_TYPE.WHATS_NEW;
  }
  if (
    [
      "trial_period_ended",
      "trial_ended",
      "poc_ended",
    ].includes(code)
  ) {
    return MODAL_TYPE.TRIAL_ENDED;
  }
  return null;
}

// Normalize the various response shapes the backend has shipped over time
// into a single { id, code } object. `id` may be null when the backend hasn't
// added it yet (legacy mark-on-fetch flow).
function parseMessagePayload(message) {
  if (message == null) return null;
  if (typeof message === "object") {
    return {
      id: message.id ?? message.message_id ?? null,
      code: message.code ?? message.type ?? null,
    };
  }
  if (typeof message === "string") {
    const trimmed = message.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const obj = JSON.parse(trimmed);
        return {
          id: obj?.id ?? obj?.message_id ?? null,
          code: obj?.code ?? obj?.type ?? null,
        };
      } catch {
        /* fall through to plain-string handling */
      }
    }
    return { id: null, code: trimmed };
  }
  return null;
}

export default function SysMessageGate() {
  const [modal, setModal] = useState({ open: false, type: null, id: null });
  const fetchedRef = useRef(false);

  // Manual re-open from anywhere in the app (e.g. the "What's new" button in
  // the sidebar). Doesn't carry an `id` because there's nothing to dismiss
  // server-side — the user is just re-reading the announcement.
  useEffect(() => {
    return onWhatsNewOpen(() => {
      setModal({ open: true, type: MODAL_TYPE.WHATS_NEW, id: null });
    });
  }, []);

  useEffect(() => {
    // Self-gate on auth + run-once. We don't fetch on /login/recover/signup.
    if (fetchedRef.current) return;
    if (!getToken()) return;
    if (typeof window !== "undefined") {
      const path = window.location.pathname || "";
      if (
        path.startsWith("/login") ||
        path.startsWith("/signin") ||
        path.startsWith("/signup") ||
        path.startsWith("/register") ||
        path.startsWith("/recover")
      ) {
        return;
      }
    }
    fetchedRef.current = true;

    let active = true;
    (async () => {
      try {
        const res = await fetch(SYS_MESSAGE_GET_URL, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }
        const json = await res.json().catch(() => null);
        if (!active) return;
        if (!json?.success || !json?.message) return;

        const payload = parseMessagePayload(json.message);
        if (!payload?.code) return;

        const type = modalTypeForCode(payload.code);
        if (!type) return;

        setModal({ open: true, type, id: payload.id });
      } catch {
        /* network failure: silently no-op so the user isn't blocked */
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const dismiss = async () => {
    const id = modal.id;
    setModal({ open: false, type: null, id: null });
    if (id == null) return; // legacy flow — backend marked seen on fetch
    try {
      await fetch(SYS_MESSAGE_DISMISS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ id }),
      });
    } catch {
      /* best-effort: backend can re-fire if dismissal failed */
    }
  };

  // What's New is a multi-slide release modal with its own layout — render it
  // directly instead of going through the FrostedModal config path.
  if (modal.type === MODAL_TYPE.WHATS_NEW) {
    return <WhatsNewModal open={modal.open} onClose={dismiss} />;
  }

  const cfg = modal.type ? MODAL_CONFIG[modal.type] : null;
  if (!cfg) return null;

  return (
    <FrostedModal
      open={modal.open}
      onClose={dismiss}
      icon={cfg.icon}
      title={cfg.title}
      eyebrow={cfg.eyebrow}
      description={cfg.description}
      primaryAction={
        cfg.primary && {
          label: cfg.primary.label,
          onClick: async () => {
            await dismiss();
            if (cfg.primary.href) {
              window.location.href = cfg.primary.href;
            }
          },
        }
      }
      secondaryAction={
        cfg.secondary && {
          label: cfg.secondary.label,
          onClick: dismiss,
        }
      }
    />
  );
}
